import {
    ChannelParametersDTO,
    WaterwaySizingParamsDTO,
    WaterwayVertexDTO,
} from 'cd-eft/_codegen_/openapi-generator/eft-set';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { observable, action } from 'mobx';
import { IWscVmProps } from "../WaterwaySizingCalculatorVM";

export interface ITrapForm {
    leftSlope: number | "" | "RESET";
    rightSlope: number | "" | "RESET";
    minBedWidth: number | "" | "RESET";
    bottomDip: string | "" | "RESET";
    dipDepth: number | "" | "RESET";
    offset: (number)[];
    freeboard: number | "" | "RESET";
}

export interface IParabolicForm {
    freeboard: number | "" | "RESET";
    limitingSlope: number | "" | "RESET";
    offset: (number)[];
}

export interface IChannelOptions {
    selectedOption: 'none' | 'fix-depth' | 'fix-width';
    fixFlowDepth: number | "" | "RESET";
    fixFlowWidth: number | "" | "RESET";
}

export class ChannelFlowVM {
    mode: 'design' | 'standalone' | 'adminGlobalPreferences' = 'standalone';
    // Common inputs
    @observable channelBedSlope: number | "" | "RESET" = "";
    @observable designDischarge: number | "" | "RESET" = "";
    @observable showDipModal: boolean = false;
    // Control and variable sets for each control option
    @observable channelControl: number = 0; // 0=Trapezoidal, 1=Parabolic
    @observable trapForm: ITrapForm = {
        leftSlope: "",
        rightSlope: "",
        minBedWidth: "",
        bottomDip: '',
        dipDepth: "",
        offset: [0.00, 0.00, 0.00],
        freeboard: ""
    }
    @observable parabolicForm: IParabolicForm = {
        freeboard: "",
        offset: [50.00, 75.00, 100.00],
        limitingSlope: ""
    }
    // Channel options control and input variables
    @observable channelOptions: IChannelOptions = {
        selectedOption: 'none',
        fixFlowDepth: "",
        fixFlowWidth: ""
    }

    @observable formValidation = {
        offset1: false,
        offset2: false,
        offset3: false,
        freeboard1: false,
        freeboard2: false,
        fixFlowDepth: false,
        fixFlowWidth: false,
        channelBedSlope: false,
        designDischarge: false,
        leftSlope: false,
        rightSlope: false,
        minBedWidth: false,
        dipDepth: false,
        limitingSlope: false,
        messages: {
            offset1: '',
            offset2: '',
            offset3: '',
            freeboard1: '',
            freeboard2: '',
            fixFlowDepth: '',
            fixFlowWidth: '',
            channelBedSlope: '',
            designDischarge: '',
            leftSlope: '',
            rightSlope: '',
            minBedWidth: '',
            dipDepth: '',
            limitingSlope: '',
        },
        channelFormErrors: false,
    };
    @observable formClassName = {
        offset1: '',
        offset2: '',
        offset3: '',
        freeboard1: '',
        freeboard2: '',
        fixFlowDepth: '',
        fixFlowWidth: '',
        channelBedSlope: '',
        designDischarge: '',
        leftSlope: '',
        rightSlope: '',
        minBedWidth: '',
        dipDepth: '',
        limitingSlope: '',
    };

    readonly bottomDipOptions = [
        {
            name: 'NO_DIP',
            value: 'NO_DIP',
            key: 'no-dip',
        },
        {
            name: 'V_DIP',
            value: 'V_DIP',
            key: 'v-dip',
        },
        {
            name: 'PARABOLIC_DIP',
            value: 'PARABOLIC_DIP',
            key: 'p-dip',
        },
    ];

    constructor(props: IWscVmProps) {this.mode = props.mode; }

    @action.bound toggleDipModal() {
        this.showDipModal = !this.showDipModal;
    }

    get trapezoidControl(): boolean {
        return this.channelControl === 0;
    }
    get parabolicControl(): boolean {
        return this.channelControl === 1;
    }
    get noneDefined(): boolean {
        return this.channelOptions.selectedOption === 'none';
    }
    get fixFlowWidth(): boolean {
        return this.channelOptions.selectedOption === 'fix-width';
    }
    get fixFlowDepth(): boolean {
        return this.channelOptions.selectedOption === 'fix-depth';
    }

    @action.bound handleOnKeyDown(event) {
        (["e", "E", "+", "-"].includes(event.key)) && event.preventDefault()
    }

    @action.bound handleInputChange(event) {
        const { name, value } = event.target;
        switch (name) {
            case 'channel-bed-slope':
                if (value < 0) { this.channelBedSlope = 0 }
                else { this.channelBedSlope = parseFloat(value) };
                break;
            case 'design-discharge':
                if (value < 0) { this.designDischarge = 0 }
                else { this.designDischarge = parseFloat(value) };
                break;
            case 'flow-depth-input':
                if (value < 0) { this.channelOptions.fixFlowDepth = 0 }
                else { this.channelOptions.fixFlowDepth = parseFloat(value) };
                break;
            case 'flow-width-input':
                if (value < 0) { this.channelOptions.fixFlowWidth = 0 }
                else { this.channelOptions.fixFlowWidth = parseFloat(value) };
                break;

        }
    }
    @action.bound handleTrapInputChange(event) {
        const { name, value } = event.target;
        switch (name) {
            case 'left-slope':
                if (value < 0) { this.trapForm.leftSlope = 0 }
                else { this.trapForm.leftSlope = parseFloat(value) };
                break;
            case 'right-slope':
                if (value < 0) { this.trapForm.rightSlope = 0 }
                else { this.trapForm.rightSlope = parseFloat(value) };
                break;
            case 'min-bed-width':
                if (value < 0) { this.trapForm.minBedWidth = 0 }
                else { this.trapForm.minBedWidth = parseFloat(value) };
                break;
            case 'bottom-dip':
                this.trapForm.bottomDip = value;
                break;
            case 'dip-depth':
                this.trapForm.dipDepth = parseFloat(value);
                break;
            case 'offset-1':
                this.trapForm.offset[0] = parseFloat(value);
                break;
            case 'offset-2':
                this.trapForm.offset[1] = parseFloat(value);
                break;
            case 'offset-3':
                this.trapForm.offset[2] = parseFloat(value);
                break;
            case 'freeboard1':
                if (value < 0) { this.trapForm.freeboard = 0 }
                else { this.trapForm.freeboard = parseFloat(value) };
                break;
        }
    }
    @action.bound handleParaInputChange(event) {
        const { name, value } = event.target;
        switch (name) {
            case 'limiting-slope':
                if (value < 0) { this.parabolicForm.limitingSlope = 0 }
                else { this.parabolicForm.limitingSlope = parseFloat(value); }
                break;
            case 'offset-1':
                if (value < 0) { this.parabolicForm.offset[0] = 0 }
                else { this.parabolicForm.offset[0] = parseFloat(value) };
                break;
            case 'offset-2':
                if (value < 0) { this.parabolicForm.offset[1] = 0 }
                else { this.parabolicForm.offset[1] = parseFloat(value) };
                break;
            case 'offset-3':
                if (value < 0) { this.parabolicForm.offset[2] = 0 }
                else { this.parabolicForm.offset[2] = parseFloat(value) };
                break;
            case 'freeboard2':
                if (value < 0) { this.parabolicForm.freeboard = 0 }
                else { this.parabolicForm.freeboard = parseFloat(value) }
                break;
        }
    }
    @action.bound selectControlType(event) {
        const { name } = event.target;
        if (name === 'parabolic') {
            this.channelControl = 1;
        } else if (name === 'trapezoid') {
            this.channelControl = 0;
        }
    }
    @action.bound selectOptionType(event) {
        const { name } = event.target;
        if (name === 'not-defined') {
            this.channelOptions.selectedOption = 'none';
        } else if (name === 'fix-flow-depth') {
            this.channelOptions.selectedOption = 'fix-depth';
        } else if (name === 'fix-flow-width') {
            this.channelOptions.selectedOption = 'fix-width';
        }
    }

    @action.bound setFixedValue(name, fixed) {
        const event2 = {
            target: {
                name: name,
                value: fixed,
            },
        };
        switch (name) {
            case 'left-slope':
            case 'right-slope':
            case 'min-bed-width':
            case 'freeboard1':
                this.handleTrapInputChange(event2);
                break;
            case 'freeboard2':
            case 'limiting-slope':
            case 'offset-1':
            case 'offset-2':
            case 'offset-3':
                this.handleParaInputChange(event2);
                break;
            case 'channel-bed-slope':
            case 'design-discharge':
            case 'flow-depth-input':
            case 'flow-width-input':
                this.handleInputChange(event2);
                break;
        }
    }

    private undefinedOrBlank(value) {
        return (value === undefined || value === '' || value === 'RESET');
    }

    @action.bound commonValidation(event) {
        const { name, value } = event.target;
        let fixed = value;
        if (!this.undefinedOrBlank(value)) {
            fixed = parseFloat(parseFloat(value).toFixed(2));
            this.setFixedValue(name, fixed);
        }
        if (name.includes('offset')) {
            const parsedName = name.replace('-', '');
            this.setToDefaultErrorLabel(
                parsedName,
                'WDT_18',
                value === undefined || fixed === '' || fixed < 0.00 || fixed > 100, name
            );
        } else {
            switch (name) {
                case 'freeboard1':
                    this.setToDefaultErrorLabel(
                        'freeboard1',
                        'WDT_41',
                        fixed !== undefined && fixed !== '' && (fixed < 0 || fixed > 10), name
                    );
                    break;
                case 'freeboard2':
                    this.setToDefaultErrorLabel(
                        'freeboard2',
                        'WDT_41',
                        fixed !== undefined && fixed !== '' && (fixed < 0 || fixed > 10), name
                    );
                    break;
                case 'flow-width-input':
                    this.setToDefaultErrorLabel(
                        'fixFlowWidth',
                        'WDT_46',
                        this.undefinedOrBlank(fixed) || fixed < 0.01 || fixed > 100.0, name
                    );
                    break;
                case 'flow-depth-input':
                    this.setToDefaultErrorLabel(
                        'fixFlowDepth',
                        'WDT_45',
                        this.undefinedOrBlank(fixed) || fixed < 0.01 || fixed > 10.0, name
                    );
                    break;
                case 'channel-bed-slope':
                    this.setToDefaultErrorLabel('channelBedSlope', 'WDT_39', this.undefinedOrBlank(fixed) || fixed < 0.01 || fixed > 100.00, name);
                    break;
                case 'design-discharge':
                    this.setToDefaultErrorLabel(
                        'designDischarge',
                        'WDT_40',
                        this.undefinedOrBlank(fixed) || fixed < 0.01 || fixed > 1000.0, name
                    );
                    break;
                case 'left-slope':
                    this.setToDefaultErrorLabel(
                        'leftSlope',
                        'WDT_42',
                        this.undefinedOrBlank(fixed) || fixed < 0.01 || fixed > 100.0, name
                    );
                    break;
                case 'right-slope':
                    this.setToDefaultErrorLabel(
                        'rightSlope',
                        'WDT_42',
                        this.undefinedOrBlank(fixed) || fixed < 0.01 || fixed > 100.0, name
                    );
                    break;
                case 'min-bed-width':
                    this.setToDefaultErrorLabel(
                        'minBedWidth',
                        'WDT_43',
                        this.undefinedOrBlank(fixed) || fixed < 0.01 || fixed > 100.0, name
                    );
                    break;
                case 'limiting-slope':
                    this.setToDefaultErrorLabel(
                        'limitingSlope',
                        'WDT_36',
                        this.undefinedOrBlank(fixed) || fixed < 0.01 || fixed > 100.0,
                        name
                    );
                    break;
            }
        }
    }

    @action.bound setToDefaultErrorLabel(name: string, code: string, predicate: boolean, elementId: string) {
        if (predicate) {
            MessageCenter.addNotificationFromCode(code);
            this.formValidation[name] = true;
            this.formValidation.channelFormErrors = true;
            this.formValidation.messages[name] = 'Please correct this field';
            this.formClassName[name] = ' usa-input--error';
        } else {
            this.formValidation[name] = false;
            this.formValidation.messages[name] = '';
            this.formClassName[name] = '';
        }
    }

    /**
     * Example of function to return a DTO based on this VMs variables and
     * what form options have been chosen.
     */
    get exportChannelFlowDto(): WaterwaySizingParamsDTO {
        const channelParams: ChannelParametersDTO = {};

        if (this.parabolicControl) {
            // if parabolic selected
            channelParams.freeBoard = (this.parabolicForm.freeboard && this.parabolicForm.freeboard !== "RESET") ? this.parabolicForm.freeboard : 0;
            channelParams.offset1 = this.parabolicForm.offset[0] / 100.00;
            channelParams.offset2 = this.parabolicForm.offset[1] / 100.00;
            channelParams.offset3 = this.parabolicForm.offset[2] / 100.00;
            channelParams.sideSlope = 0.5;
            channelParams.channelShapeStr = 'PARABOLIC';
            // Zero Trapezoidal specific values
            channelParams.leftSlopeRatio = (this.parabolicForm.limitingSlope && this.parabolicForm.limitingSlope !== "RESET") ? this.parabolicForm.limitingSlope : 0.5;
            channelParams.rightSlopeRatio = 0;
            channelParams.minBedWidth = 0;
        } else {
            // if trapezoidal selected
            channelParams.freeBoard = (this.trapForm.freeboard && this.trapForm.freeboard !== "RESET") ? this.trapForm.freeboard : 0;
            channelParams.leftSlopeRatio = (this.trapForm.leftSlope && this.trapForm.leftSlope !== "RESET") ? this.trapForm.leftSlope : 0;
            channelParams.rightSlopeRatio = (this.trapForm.rightSlope && this.trapForm.rightSlope !== "RESET") ? this.trapForm.rightSlope : 0;
            channelParams.minBedWidth = (this.trapForm.minBedWidth && this.trapForm.minBedWidth !== "RESET") ? this.trapForm.minBedWidth : 0;
            channelParams.channelShapeStr = 'TRAPEZOIDAL';
            // Zero Parabolic specific values
            channelParams.offset1 = 0;
            channelParams.offset2 = 0;
            channelParams.offset3 = 0;
            channelParams.sideSlope = 0.5;
        }
        // defaults
        channelParams.backSlopeRatio = 4.0;
        channelParams.bermWidth = 3.0;
        channelParams.computeEndShape = true;
        channelParams.computeStartShape = true;
        channelParams.endSlopeDistance = 20.0;
        channelParams.sideWidthLimit = 30.0;
        channelParams.startSlopeDistance = 20.0;
        channelParams.bottomDipStr = "NO_DIP";
        channelParams.dipDepth = 0.0;

        const dataVertex: WaterwayVertexDTO = {
            totalQ: (this.designDischarge && this.designDischarge !== "RESET") ? this.designDischarge : 0,
        };

        if (this.channelOptions.selectedOption === 'fix-depth') {
            dataVertex.depth = (this.channelOptions.fixFlowDepth && this.channelOptions.fixFlowDepth !== "RESET") ? this.channelOptions.fixFlowDepth : 0;
            dataVertex.width = 0;
            dataVertex.depthEdited = true;
            dataVertex.widthEdited = false;
        } else if (this.channelOptions.selectedOption === 'fix-width') {
            dataVertex.width = (this.channelOptions.fixFlowWidth && this.channelOptions.fixFlowWidth !== "RESET") ? this.channelOptions.fixFlowWidth : 0;
            dataVertex.depth = 0;
            dataVertex.widthEdited = true;
            dataVertex.depthEdited = false;
        }
        else {
            dataVertex.width = 0;
            dataVertex.depth = 0;
            dataVertex.widthEdited = false;
            dataVertex.depthEdited = false;
        }

        const waterwayParams: WaterwaySizingParamsDTO = {
            channelParams: channelParams,
            channelSlope: (this.channelBedSlope && this.channelBedSlope !== "RESET") ? this.channelBedSlope : 0,
            dataVertex: dataVertex,
        };
        return waterwayParams;
    }


    private callValidation(name, value) {
        const event = {
            target: {
                name: name,
                value: value,
            },
        };
        this.commonValidation(event);
    }
    @action.bound validateAllSelected(): boolean {
        if (this.parabolicControl) {
            this.callValidation('freeboard2', this.parabolicForm.freeboard);
            this.callValidation('offset-1', this.parabolicForm.offset[0]);
            this.callValidation('offset-2', this.parabolicForm.offset[1]);
            this.callValidation('offset-3', this.parabolicForm.offset[2]);
            this.callValidation('limiting-slope', this.parabolicForm.limitingSlope);
        } else {
            // if trapezoidal selected
            this.callValidation('freeboard1', this.trapForm.freeboard);
            this.callValidation('left-slope', this.trapForm.leftSlope);
            this.callValidation('right-slope', this.trapForm.rightSlope);
            this.callValidation('min-bed-width', this.trapForm.minBedWidth);
        }

        if (this.channelOptions.selectedOption === 'fix-depth') {
            this.callValidation('flow-depth-input', this.channelOptions.fixFlowDepth);
        } else if (this.channelOptions.selectedOption === 'fix-width') {
            this.callValidation('flow-width-input', this.channelOptions.fixFlowWidth);
        }
        if(this.mode !== 'adminGlobalPreferences'){            
            this.callValidation('design-discharge', this.designDischarge);
            this.callValidation('channel-bed-slope', this.channelBedSlope);
            this.validateForm();
        }else{
            return this.validateForm_Flow || this.validateForm_Parabolic || this.validateForm_Trapezoidal; 
        }
        return this.formValidation.channelFormErrors;
    }
    private get validateForm_Parabolic() {
        const { offset1, offset2, offset3, limitingSlope, freeboard2 } = this.formValidation;
        if (this.channelControl === 1 && (offset1 || offset2 || offset3 || limitingSlope || freeboard2)) {
            return true;
        } else return false;
    }
    private get validateForm_Trapezoidal() {
        const { minBedWidth, freeboard1, leftSlope, rightSlope } = this.formValidation;
        if (this.channelControl === 0 && (minBedWidth || freeboard1 || leftSlope || rightSlope)) {
            return true;
        } else return false;
    }
    private get validateForm_Flow() {
        if (this.formValidation.fixFlowDepth && this.channelOptions.selectedOption === 'fix-depth') {
            return true;
        } else if (this.formValidation.fixFlowWidth && this.channelOptions.selectedOption === 'fix-width') {
            return true;
        } else return false;
    }
    @action.bound validateForm() {
        this.formValidation.channelFormErrors =
            this.formValidation.channelBedSlope ||
            this.formValidation.designDischarge ||
            this.validateForm_Flow ||
            this.validateForm_Parabolic ||
            this.validateForm_Trapezoidal;
        return this.formValidation.channelFormErrors;
    }
}
